import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ProfileForm from '../influencer/ProfileForm';
import { graphqlApi } from '../../../services/graphql';
import { useAuthStore } from '../../../store/authStore';
import type { ProfileFormData } from '../../../types';

/**
 * Host/Agency My Information page.
 * Same form as influencer but fetches/saves only from the users table.
 */
const HostMyInformationPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const { user } = useAuthStore();

  const [formData, setFormData] = useState<ProfileFormData | undefined>();
  const [social, setSocial] = useState<{ oauthProvider?: string; kakaoId?: string; naverId?: string; googleId?: string; facebookId?: string }>({});
  // 휴대폰 본인확인 (NICE) state. Kept out of `formData` on purpose: the 기본정보
  // 저장 button persists ProfileFormData, and identity is applied immediately by
  // its own mutation rather than by the save button.
  const [identity, setIdentity] = useState<{ identityVerified?: boolean; identityName?: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user?.userId) { setError('User not authenticated'); setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      const res = await graphqlApi.getUser(user.userId);
      if (res.success && res.data) {
        const ud = (res.data as any).getUser || res.data;
        setFormData({
          name: ud.name || '',
          nickname: ud.nickname || '',
          phoneNumber: ud.phoneNumber || '',
          phoneVerified: ud.phoneVerified || false,
          marketingConsentCollection: ud.marketingConsentCollection || false,
          marketingConsentPromotion: ud.marketingConsentPromotion || false,
          marketingConsentEmail: ud.marketingConsentEmail || false,
          marketingConsentSMS: ud.marketingConsentSMS || false,
        });
        setSocial({
          oauthProvider: ud.oauthProvider,
          kakaoId: ud.kakaoId,
          naverId: ud.naverId,
          googleId: ud.googleId,
          facebookId: ud.facebookId,
        });
        setIdentity({
          identityVerified: ud.identityVerified,
          identityName: ud.identityName,
        });
      } else {
        setError('Failed to load profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Toast after returning from a 간편로그인 연결 round-trip
  // (OAuthCallbackHandler redirects back here with ?connect=<provider>&status=...).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const connect = searchParams.get('connect');
    const status = searchParams.get('status');
    if (!connect || !status) return;
    if (status === 'success') {
      toast.success(t('myProfile.form.socialConnected', '간편로그인이 연결되었습니다.'));
      fetchData();
    } else if (status === 'cancelled') {
      // The user dismissed the provider's consent screen. Deliberate, so it
      // gets no toast at all — telling them it "failed" would be wrong.
    } else if (status === 'conflict') {
      toast.error(t('myProfile.form.socialConflict', '이미 다른 계정에 연결된 계정입니다.'));
    } else {
      toast.error(t('myProfile.form.socialConnectFailed', '간편로그인 연결에 실패했습니다.'));
    }
    searchParams.delete('connect');
    searchParams.delete('status');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams]);

  const handleSubmit = useCallback(async (data: ProfileFormData) => {
    if (!user?.userId) return;
    await graphqlApi.updateUser({
      userId: user.userId,
      name: data.name,
      nickname: data.nickname,
      marketingConsentCollection: data.marketingConsentCollection,
      marketingConsentPromotion: data.marketingConsentPromotion,
      marketingConsentEmail: data.marketingConsentEmail,
      marketingConsentSMS: data.marketingConsentSMS,
    } as any);
  }, [user?.userId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-2 text-gray-600">{t('myProfile.form.loading', 'Loading...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
          {t('myProfile.form.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <ProfileForm
      initialData={formData}
      onSubmit={handleSubmit}
      isLoading={loading}
      hidePhoneVerification
      showIdentityVerification
      identityVerified={!!identity.identityVerified}
      onIdentityVerified={fetchData}
      oauthProvider={social.oauthProvider}
      kakaoLinked={!!social.kakaoId || social.oauthProvider?.toLowerCase() === 'kakao'}
      naverLinked={!!social.naverId || social.oauthProvider?.toLowerCase() === 'naver'}
      googleLinked={!!social.googleId || social.oauthProvider?.toLowerCase() === 'google'}
      facebookLinked={!!social.facebookId || social.oauthProvider?.toLowerCase() === 'facebook'}
      onSocialChanged={fetchData}
    />
  );
};

export default HostMyInformationPage;
